/**
 * How the notification bell presents a notification (redesign 2026-10-12,
 * modelled on Reverb's: a pill count on the bell, Matches / Offers / Trades
 * shortcut rows with unread counts, then an Updates feed with a pedal
 * thumbnail wherever the notification is about a pedal).
 *
 * Pure, so the grouping and fallbacks are unit-tested without the dropdown.
 */

const MATCH_TYPES = new Set(['new_match', 'new_match_batch', 'want_list_match', 'want_list_price_drop']);
const OFFER_TYPES = new Set([
  'new_offer', 'new_purchase_offer', 'offer_comment', 'purchase_offer_comment',
  'offer_viewed', 'offer_expired', 'offer_sent', 'offers_sent_bulk', 'listing_interest',
  // A comment on a match is pre-offer negotiation: it groups with offer
  // comments here and shares their push preference category
  // ('offerResponses' in functions/sendPushOnNotification.js), so muting one
  // mutes both and the bell and the phone agree (2026-11-01 review).
  'match_comment',
]);
const TRADE_TYPES = new Set([
  'ready_to_ship', 'tracking_added_trade', 'tracking_added_purchase',
  'package_delivered_trade', 'package_delivered_purchase',
  'trade_complete', 'purchase_complete', 'step_reminder', 'new_message',
]);
const STATUS_CHANGE_TYPES = new Set(['offer_status_change', 'purchase_offer_status_change']);

/** 'matches' | 'offers' | 'trades' | null (comments, welcome, billing...). */
export function notificationCategory(n = {}) {
  const { type } = n;
  if (MATCH_TYPES.has(type)) return 'matches';
  if (OFFER_TYPES.has(type)) return 'offers';
  if (TRADE_TYPES.has(type)) return 'trades';
  // An accepted offer becomes a trade (and routes to the accepted pages);
  // counters and declines are still offer business.
  if (STATUS_CHANGE_TYPES.has(type)) return n.metadata?.action === 'accepted' ? 'trades' : 'offers';
  return null;
}

/** Where each shortcut row goes. Trades opens Accepted Trades (owner, 2026-10-12; was the Trading hub). */
export const CATEGORY_ROWS = [
  { key: 'matches', label: 'Matches', path: '/matches' },
  { key: 'offers', label: 'Offers', path: '/trade-center' },
  { key: 'trades', label: 'Trades', path: '/accepted-trades' },
];

export function unreadCountsByCategory(notifications = []) {
  const counts = { matches: 0, offers: 0, trades: 0 };
  for (const n of notifications) {
    if (n.read) continue;
    const c = notificationCategory(n);
    if (c) counts[c] += 1;
  }
  return counts;
}

/** Badge text: nothing at 0, the number up to 9, then "10+" (owner's pick, matching Reverb). */
export function formatBadgeCount(count) {
  const n = Number(count) || 0;
  if (n <= 0) return '';
  return n > 9 ? '10+' : String(n);
}

function imageOf(pedal) {
  if (!pedal || typeof pedal !== 'object') return null;
  return pedal.images?.top || pedal.images?.front || pedal.imageUrl
    || pedal.placeholderImage?.url || pedal.placeholderUrl || null;
}

/**
 * The pedal photo to show, or null. New notifications carry metadata.imageUrl
 * (written server-side since 2026-10-12); older match notifications embed the
 * matched pedal, so they get a photo too. Everything else falls back to an icon.
 */
export function notificationImage(n = {}) {
  const m = n.metadata || {};
  if (typeof m.imageUrl === 'string' && m.imageUrl) return m.imageUrl;
  return imageOf(m.matchedPedal) || imageOf(m.pedalTheyWant) || null;
}

/** Icon for notifications without a photo. */
export function notificationIcon(n = {}) {
  switch (n.type) {
    case 'want_list_match':
    case 'want_list_price_drop':
      return 'heart';
    case 'new_match':
    case 'new_match_batch':
      return 'matches';
    case 'step_reminder':
    case 'offer_expired':
      return 'clock';
    case 'ready_to_ship':
    case 'tracking_added_trade':
    case 'tracking_added_purchase':
    case 'package_delivered_trade':
    case 'package_delivered_purchase':
      return 'box';
    case 'trade_complete':
    case 'purchase_complete':
    case 'referral_completed':
    case 'early_bird_earned':
      return 'star';
    case 'new_message':
    case 'offer_comment':
    case 'purchase_offer_comment':
    case 'listing_comment':
    case 'comment_mention':
    case 'match_comment':
      return 'chat';
    default:
      if (typeof n.type === 'string' && n.type.startsWith('billing_')) return 'card';
      if (notificationCategory(n) === 'offers') return 'offers';
      if (notificationCategory(n) === 'trades') return 'trades';
      return 'bell';
  }
}

/**
 * The trade pedal that matters to `uid`: the one they are trading FOR (owner,
 * 2026-10-12), as { image } or, when only its listing is known, { listingId }.
 * The initiator (fromUserId) receives the responder's pedal; the responder
 * receives the initiator's (first, if several). A cash-only side receives no
 * pedal, so it falls back to the other one to still show a photo. A counter
 * rewrites initiatorPedalIds but not pedalSnapshots, so a snapshot no longer
 * in the deal is skipped in favour of the current pedal's listing.
 * Mirrors tradePedalFor in functions/notificationImage.js.
 */
export function tradePhotoFor(trade, uid) {
  if (!trade) return {};
  const snaps = trade.pedalSnapshots || {};
  const candidates = [...(Array.isArray(snaps.initiatorPedals) ? snaps.initiatorPedals : []), snaps.initiatorPedal].filter(Boolean);
  const currentIds = (Array.isArray(trade.initiatorPedalIds) ? trade.initiatorPedalIds : [trade.initiatorPedalId || trade.fromPedalId]).filter(Boolean);
  let initiator = candidates[0] ? { image: imageOf(candidates[0]) } : null;
  if (currentIds.length && candidates.some((p) => p.id)) {
    const current = candidates.find((p) => currentIds.includes(p.id));
    initiator = current ? { image: imageOf(current) } : { listingId: currentIds[0] };
  }
  const responder = snaps.responderPedal ? { image: imageOf(snaps.responderPedal) } : null;
  const isInitiator = uid && uid === trade.fromUserId;
  const [incoming, other] = isInitiator ? [responder, initiator] : [initiator, responder];
  const usable = (x) => x && (x.image || x.listingId);
  return (usable(incoming) && incoming) || (usable(other) && other) || {};
}

/** Just the photo, when the snapshots carry it (see tradePhotoFor). */
export function tradeImageFor(trade, uid) {
  return tradePhotoFor(trade, uid).image || null;
}

/**
 * What to look up for a notification that carries no photo, as a cache key
 * 'kind:id'. A named listing first, then the trade, purchase or interest it
 * belongs to. Null when there is nothing to look up.
 */
export function imageLookupKey(n = {}) {
  const m = n.metadata || {};
  if (m.listingId) return `listing:${m.listingId}`;
  if (n.tradeId) return `trade:${n.tradeId}`;
  const purchaseId = n.purchaseId || m.purchaseId || n.offerId || m.purchaseOfferId || n.purchaseOfferId;
  if (purchaseId) return `purchase:${purchaseId}`;
  const interestId = m.respondingToInterestId || m.interestId || m.latestInterestId;
  if (interestId) return `interest:${interestId}`;
  return null;
}
